import { useCallback, useRef } from 'react';

/**
 * Custom debounced callback hook
 * Delays the execution of a callback function until after a specified delay has passed
 * since the last time it was invoked.
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced callback function
 */
export const useDebouncedCallback = <T extends (...args: any[]) => any>(callback: T, delay: number): T => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }) as T,
    [callback, delay],
  );
};
